import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  ScrollView,
  ActivityIndicator,
  Platform,
  Share,
} from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import { Ionicons } from '@expo/vector-icons';
import {
  Header,
  ScreenLayout,
  Modal,
  PaneHeader,
  PaneHeaderIconButton,
  colors,
  spacing,
  typography,
} from '../components';
import { VoiceNotePlayer } from '../components/conversation-cards';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import type { TranslationKey } from '../i18n';
import { useThemedStyles } from '../hooks/useThemedStyles';

// The recording endpoint is auth-protected (unlike the public media-storage
// proxy MessageBubble's voice notes use), and browsers cannot attach a
// custom Authorization header to an <audio>/download request — confirmed
// expo-av's web build silently ignores Sound.createAsync's `headers` option.
// So playback/download both go through this: fetch the recording once with
// a proper Bearer token, then hand VoiceNotePlayer/the download button a
// local, already-authenticated URI (blob: on web, a temp file on native) —
// nothing is written to permanent/user-visible storage, matching "don't
// save it locally, get it from InnoCalls".
type RecordingFetchState = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

const useCallRecording = (callId: string | null) => {
  const [state, setState] = useState<RecordingFetchState>('idle');
  const [localUri, setLocalUri] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Revoke the previous call's blob URL (web) so it doesn't leak — a fresh
    // one is created per callId below when the recording is actually loaded.
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [callId]);

  const load = useCallback(async () => {
    if (!callId || state === 'loading' || state === 'ready') return;
    setState('loading');
    try {
      const token = apiService.getAuthToken();
      const url = `${apiService.getBaseURL()}/api/phone/calls/${callId}/recording`;
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (response.status === 404 || response.status === 409) {
        setState('unavailable');
        return;
      }
      if (!response.ok) {
        setState('error');
        return;
      }

      if (Platform.OS === 'web') {
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setLocalUri(objectUrl);
      } else {
        // expo-file-system has no "fetch with auth headers into a blob"
        // primitive, so download to the app's cache dir (not user-visible
        // downloads) using the same Bearer token.
        const dir = `${FileSystem.cacheDirectory}call-recordings`;
        const dirInfo = await FileSystem.getInfoAsync(dir);
        if (!dirInfo.exists) await FileSystem.makeDirectoryAsync(dir, { intermediates: true });
        const localPath = `${dir}/${callId}.wav`;
        const { uri } = await FileSystem.downloadAsync(url, localPath, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        setLocalUri(uri);
      }
      setState('ready');
    } catch (error) {
      console.warn('[CallHistoryScreen] Failed to load call recording:', error);
      setState('error');
    }
  }, [callId, state]);

  return { state, localUri, load };
};

// Committed directly to InnoCalls' own SDK outcomes (see usePhoneDialer.ts):
//  - 'completed'      call ran its course (InnoCalls' own onCallEnded)
//  - 'not-responded'  callee never picked up (onCallMissed)
//  - 'cancelled'      callee busy/declined (onCallRejected)
//  - 'ended'          the agent hung up before InnoCalls reported an outcome
//  - 'failed'         technical failure, not a call outcome
//  - 'ringing'/'answered' in-progress states, shouldn't normally appear in history
type CallStatus = 'ringing' | 'answered' | 'completed' | 'not-responded' | 'cancelled' | 'ended' | 'failed';

interface CallOutcomeOption {
  id: string;
  label: string;
  labelAr?: string;
  icon?: string;
  color?: string;
  resultType: 'success' | 'neutral' | 'failure';
  isActive: boolean;
}

interface CallRecord {
  _id: string;
  phoneNumber: string;
  callerNumber?: string;
  direction: 'inbound' | 'outbound';
  status: CallStatus;
  duration?: number;
  startedAt: string;
  endedAt?: string;
  assignedTo?: string;
  provider?: string;
  outcomeId?: string;
  outcomeRecordedAt?: string;
  outcomeNote?: string;
}

const formatDuration = (seconds?: number): string => {
  const s = seconds ?? 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
};

const formatFullDateTime = (iso: string): string => {
  const date = new Date(iso);
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const STATUS_COLOR_ICON: Record<CallStatus, { color: string; icon: any }> = {
  completed: { color: '#22C55E', icon: 'checkmark-circle' },
  'not-responded': { color: '#EF4444', icon: 'call-outline' },
  cancelled: { color: '#F59E0B', icon: 'close-circle' },
  ended: { color: '#9CA3AF', icon: 'call-outline' },
  failed: { color: '#EF4444', icon: 'warning' },
  ringing: { color: '#F59E0B', icon: 'call' },
  answered: { color: '#22C55E', icon: 'call' },
};

const STATUS_LABEL_KEY: Record<CallStatus, TranslationKey> = {
  completed: 'callHistory.status.completed',
  'not-responded': 'callHistory.status.notResponded',
  cancelled: 'callHistory.status.cancelled',
  ended: 'callHistory.status.ended',
  failed: 'callHistory.status.failed',
  ringing: 'callHistory.status.ringing',
  answered: 'callHistory.status.answered',
};

const getDirectionIcon = (call: CallRecord): { name: any; color: string } => ({
  name: call.direction === 'inbound' ? 'arrow-down-circle' : 'arrow-up-circle',
  color: STATUS_COLOR_ICON[call.status]?.color ?? '#6B7280',
});

const CallDetailsPanel: React.FC<{
  call: CallRecord;
  onCallBack: (toNumber?: string) => void;
  onOutcomeRecorded: (callId: string, patch: Partial<CallRecord>) => void;
}> = ({ call, onCallBack, onOutcomeRecorded }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { hasPermissionAtLeast } = usePermissions();
  const [outcomeOptions, setOutcomeOptions] = useState<CallOutcomeOption[] | null>(null);
  const [showOutcomePicker, setShowOutcomePicker] = useState(false);
  const [recordingOutcomeId, setRecordingOutcomeId] = useState<string | null>(null);

  useEffect(() => {
    // Fetch once this call either has a recorded outcome to resolve/display,
    // or the picker is opened to record one — no need otherwise.
    if (outcomeOptions || (!showOutcomePicker && !call.outcomeId)) return;
    apiService.getPhoneOutcomesConfig()
      .then((res) => {
        if (res.statusCode === 1000 && res.data?.outcomes) setOutcomeOptions(res.data.outcomes);
      })
      .catch((error) => console.warn('[CallHistoryScreen] Failed to load outcomes config:', error));
  }, [showOutcomePicker, outcomeOptions, call.outcomeId]);

  const handlePickOutcome = useCallback(async (outcomeId: string) => {
    setRecordingOutcomeId(outcomeId);
    try {
      const res = await apiService.recordCallOutcome(call._id, outcomeId);
      if (res.statusCode === 1000 && res.data) {
        onOutcomeRecorded(call._id, {
          outcomeId: res.data.outcomeId,
          outcomeRecordedAt: res.data.outcomeRecordedAt,
        });
        setShowOutcomePicker(false);
      }
    } catch (error) {
      console.warn('[CallHistoryScreen] Failed to record call outcome:', error);
    } finally {
      setRecordingOutcomeId(null);
    }
  }, [call._id, onOutcomeRecorded]);

  const recordedOutcome = outcomeOptions?.find((o) => o.id === call.outcomeId);
  // phone.playRecording gates the server endpoint itself (no permission =
  // the fetch 403s before this screen ever gets a recording to show) —
  // phone.downloadRecording is a separate, client-side-only gate on top of
  // that, since save/share never hits a distinct server endpoint (the
  // fetched recording is just handed to the OS share sheet or a browser
  // download link locally).
  const canDownloadRecording = hasPermissionAtLeast('phone.downloadRecording', 'edit');
  const meta = STATUS_COLOR_ICON[call.status] ?? STATUS_COLOR_ICON.ended;
  const statusLabel = t(STATUS_LABEL_KEY[call.status] ?? STATUS_LABEL_KEY.ended);
  const canHaveRecording = call.status === 'completed';
  const recording = useCallRecording(canHaveRecording ? call._id : null);

  const handleDownload = useCallback(async () => {
    if (recording.state !== 'ready' || !recording.localUri) return;
    const filename = `call-${new Date(call.startedAt).toISOString().slice(0, 19).replace(/[:T]/g, '-')}.wav`;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const a = document.createElement('a');
      a.href = recording.localUri;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      try {
        await Share.share({ url: recording.localUri });
      } catch (error) {
        console.warn('[CallHistoryScreen] Failed to share/save recording:', error);
      }
    }
  }, [recording.state, recording.localUri, call.startedAt]);

  return (
    <View style={styles.detailsContainer}>
      <View style={styles.detailsHeader}>
        <View style={[styles.detailsIconWrap, { backgroundColor: meta.color + '20' }]}>
          <Ionicons name={call.direction === 'inbound' ? 'arrow-down-circle' : 'arrow-up-circle'} size={28} color={meta.color} />
        </View>
        <Text style={[styles.detailsDirection, { textAlign: rtl.textStart }]}>{call.direction === 'inbound' ? t('callHistory.incomingCall') : t('callHistory.outgoingCall')}</Text>
        <View style={[styles.detailsStatusBadge, { backgroundColor: meta.color + '20' }]}>
          <Text style={[styles.detailsStatusText, { color: meta.color }]}>{statusLabel}</Text>
        </View>
      </View>

      <View style={styles.detailsRows}>
        <View style={[styles.detailsRow, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.detailsRowLabel, { textAlign: rtl.textStart }]}>{t('callHistory.details.duration')}</Text>
          <Text style={[styles.detailsRowValue, { textAlign: rtl.textStart }]}>{formatDuration(call.duration)}</Text>
        </View>
        <View style={[styles.detailsRow, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.detailsRowLabel, { textAlign: rtl.textStart }]}>{t('callHistory.details.started')}</Text>
          <Text style={[styles.detailsRowValue, { textAlign: rtl.textStart }]}>{formatFullDateTime(call.startedAt)}</Text>
        </View>
        {!!call.endedAt && (
          <View style={[styles.detailsRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.detailsRowLabel, { textAlign: rtl.textStart }]}>{t('callHistory.details.ended')}</Text>
            <Text style={[styles.detailsRowValue, { textAlign: rtl.textStart }]}>{formatFullDateTime(call.endedAt)}</Text>
          </View>
        )}
        <View style={[styles.detailsRow, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.detailsRowLabel, { textAlign: rtl.textStart }]}>{t('callHistory.details.number')}</Text>
          <Text style={[styles.detailsRowValue, { textAlign: rtl.textStart }]}>{call.phoneNumber}</Text>
        </View>
        {!!call.provider && (
          <View style={[styles.detailsRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.detailsRowLabel, { textAlign: rtl.textStart }]}>{t('callHistory.details.provider')}</Text>
            <Text style={[styles.detailsRowValue, { textAlign: rtl.textStart }]}>{call.provider}</Text>
          </View>
        )}
      </View>

      {canHaveRecording && (
        <View style={styles.recordingSection}>
          <Text style={[styles.recordingSectionLabel, { textAlign: rtl.textStart }]}>{t('callHistory.recording.label')}</Text>
          {recording.state === 'idle' && (
            <TouchableOpacity style={[styles.recordingLoadButton, { flexDirection: rtl.rowDirection }]} onPress={recording.load} activeOpacity={0.8}>
              <Ionicons name="play-circle-outline" size={20} color={colors.primary as string} />
              <Text style={styles.recordingLoadButtonText}>{t('callHistory.recording.play')}</Text>
            </TouchableOpacity>
          )}
          {recording.state === 'loading' && (
            <View style={[styles.recordingLoadButton, { flexDirection: rtl.rowDirection }]}>
              <ActivityIndicator size="small" color={colors.primary as string} />
              <Text style={styles.recordingLoadButtonText}>{t('callHistory.recording.loading')}</Text>
            </View>
          )}
          {recording.state === 'unavailable' && (
            <Text style={[styles.recordingUnavailableText, { textAlign: rtl.textStart }]}>{t('callHistory.recording.unavailable')}</Text>
          )}
          {recording.state === 'error' && (
            <TouchableOpacity style={[styles.recordingLoadButton, { flexDirection: rtl.rowDirection }]} onPress={recording.load} activeOpacity={0.8}>
              <Ionicons name="refresh" size={20} color={colors.error as string} />
              <Text style={[styles.recordingLoadButtonText, { color: colors.error as string }]}>{t('callHistory.recording.retry')}</Text>
            </TouchableOpacity>
          )}
          {recording.state === 'ready' && recording.localUri && (
            <View style={[styles.recordingPlayerRow, { flexDirection: rtl.rowDirection }]}>
              <View style={styles.recordingPlayerWrap}>
                <VoiceNotePlayer mediaUrl={recording.localUri} isOutgoing={false} />
              </View>
              {canDownloadRecording && (
                <TouchableOpacity style={styles.recordingDownloadButton} onPress={handleDownload} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="download-outline" size={20} color={colors.primary as string} />
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      )}

      <View style={styles.outcomeSection}>
        <Text style={[styles.recordingSectionLabel, { textAlign: rtl.textStart }]}>{t('callHistory.outcome.label')}</Text>
        {call.outcomeId && recordedOutcome ? (
          <View style={[styles.outcomeChip, { flexDirection: rtl.rowDirection, backgroundColor: (recordedOutcome.color || (colors.primary as string)) + '20' }]}>
            {!!recordedOutcome.icon && <Ionicons name={recordedOutcome.icon as any} size={16} color={recordedOutcome.color || (colors.primary as string)} />}
            <Text style={[styles.outcomeChipText, { color: recordedOutcome.color || (colors.primary as string) }]}>{recordedOutcome.label}</Text>
          </View>
        ) : call.outcomeId ? (
          // Outcome recorded but options haven't resolved yet (loading, or a
          // custom outcome an in-flight config edit removed) — still show
          // something rather than nothing.
          <Text style={[styles.detailsRowValue, { textAlign: rtl.textStart }]}>{t('callHistory.outcome.recorded')}</Text>
        ) : !showOutcomePicker ? (
          <TouchableOpacity style={[styles.recordingLoadButton, { flexDirection: rtl.rowDirection }]} onPress={() => setShowOutcomePicker(true)} activeOpacity={0.8}>
            <Ionicons name="checkmark-done-outline" size={20} color={colors.primary as string} />
            <Text style={styles.recordingLoadButtonText}>{t('callHistory.outcome.record')}</Text>
          </TouchableOpacity>
        ) : !outcomeOptions ? (
          <ActivityIndicator size="small" color={colors.primary as string} />
        ) : (
          <View style={[styles.outcomeOptionsRow, { flexDirection: rtl.rowDirection }]}>
            {outcomeOptions.filter((o) => o.isActive).map((option) => (
              <TouchableOpacity
                key={option.id}
                style={[styles.outcomeChip, styles.outcomeChipSelectable, { flexDirection: rtl.rowDirection, borderColor: option.color || colors.border as string }]}
                onPress={() => handlePickOutcome(option.id)}
                disabled={recordingOutcomeId !== null}
                activeOpacity={0.75}
              >
                {recordingOutcomeId === option.id ? (
                  <ActivityIndicator size="small" color={option.color || (colors.primary as string)} />
                ) : (
                  <>
                    {!!option.icon && <Ionicons name={option.icon as any} size={14} color={option.color || (colors.primary as string)} />}
                    <Text style={[styles.outcomeChipText, { color: option.color || (colors.primary as string) }]}>{option.label}</Text>
                  </>
                )}
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      <TouchableOpacity style={[styles.detailsCallButton, { flexDirection: rtl.rowDirection }]} onPress={() => onCallBack(call.phoneNumber)} activeOpacity={0.85}>
        <Ionicons name="call" size={18} color="#FFFFFF" />
        <Text style={styles.detailsCallButtonText}>{t('callHistory.callBack')}</Text>
      </TouchableOpacity>
    </View>
  );
};

interface CallHistoryScreenProps {
  /** Renders inline in a host pane (the contact profile's right pane) instead
   * of as its own full screen — mirrors ConversationScreen.tsx's identical
   * `embedded` convention: no ScreenLayout/own left-right panes (a pane
   * can't sanely nest another pane split), contactId/contactName/phoneNumber
   * come from props instead of routeParams, and the call-details view always
   * renders as a Modal (the nested right-pane branch below has nowhere to go
   * inside a pane that's already someone else's right pane). */
  embedded?: boolean;
  contactId?: string;
  contactName?: string;
  phoneNumber?: string;
  phoneIndex?: number;
  onClose?: () => void;
  hideEmbeddedHeader?: boolean;
}

/**
 * Full call history for one contact — opened by tapping a phone-channel row
 * in the Inbox (calls have no chat thread, so they route here instead of
 * ConversationScreen), or embedded directly in a contact profile's pane (see
 * `embedded` above). Reads contactId/contactName/phoneNumber from routeParams
 * when not embedded, since AppNavigator only captures the 2nd navigateTo()
 * arg into state for screen === 'conversation'. Selecting a call row shows
 * its full details in the right pane (web, non-embedded) or a Modal
 * (native, and always when embedded).
 */
export const CallHistoryScreen: React.FC<CallHistoryScreenProps> = ({ embedded = false, contactId: contactIdProp, contactName: contactNameProp, phoneNumber: phoneNumberProp, phoneIndex: phoneIndexProp, onClose, hideEmbeddedHeader = false }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, setActiveTab, activeTab, routeParams, startPhoneCall, isPhoneDialerReady } = useAppContext();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const contactId = embedded ? contactIdProp : ((routeParams as any)?.contactId as string | undefined);
  const contactName = embedded ? contactNameProp : ((routeParams as any)?.contactName as string | undefined);
  const phoneNumber = embedded ? phoneNumberProp : ((routeParams as any)?.phoneNumber as string | undefined);
  const routePhoneIndex = (routeParams as any)?.phoneIndex;
  const phoneIndex = embedded
    ? phoneIndexProp
    : (typeof routePhoneIndex === 'number' ? routePhoneIndex : routePhoneIndex !== undefined ? Number(routePhoneIndex) : undefined);

  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const handleOutcomeRecorded = useCallback((callId: string, patch: Partial<CallRecord>) => {
    setCalls((prev) => prev.map((c) => (c._id === callId ? { ...c, ...patch } : c)));
  }, []);

  useEffect(() => {
    if (!embedded) setActiveTab('chat');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCalls = useCallback(async () => {
    if (!contactId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setSelectedCallId(null);
    try {
      const res = await apiService.getPhoneCalls({ contactId, phoneIndex, limit: 100 });
      const data: CallRecord[] = Array.isArray(res?.data) ? res.data : [];
      setCalls(data);
      if (!embedded && Platform.OS === 'web' && data.length > 0) {
        setSelectedCallId(data[0]._id);
      }
    } catch (err) {
      console.warn('[CallHistoryScreen] Failed to load call history:', err);
      setCalls([]);
    } finally {
      setIsLoading(false);
    }
  }, [contactId, phoneIndex, embedded]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  // toNumber/phoneNumber here may be the DISPLAY-masked string (this screen's
  // routeParams.phoneNumber and each CallRecord.phoneNumber are both already
  // correctly masked server-side for users without contacts.viewMobileNumber —
  // see getUnifiedConversations/phone.controller.ts's maskPhoneValue calls) —
  // so dialing must always resolve the real number via the dedicated
  // dial-target endpoint rather than trust either of those fields directly.
  const handleCallBack = useCallback(async (toNumber?: string) => {
    if (!isPhoneDialerReady) {
      console.warn('[CallHistoryScreen] Call-back pressed before phone dialer is ready — ignoring press');
      return;
    }
    let target: string | undefined;
    if (contactId) {
      try {
        const res = await apiService.getContactDialTarget(contactId, undefined, undefined, undefined, phoneIndex);
        target = res.data?.phoneNumber;
      } catch (error) {
        console.error('[CallHistoryScreen] Failed to resolve dial target for contact', contactId, error);
      }
    }
    if (!target) {
      target = toNumber || phoneNumber;
    }
    if (!target) return;
    startPhoneCall(target, contactId, { name: contactName || target });
  }, [contactId, contactName, phoneNumber, phoneIndex, startPhoneCall, isPhoneDialerReady]);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const selectedCall = calls.find((c) => c._id === selectedCallId) || null;

  const titleContent = (
    <View style={styles.titleInfo}>
      <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{contactName || phoneNumber || t('callHistory.title')}</Text>
      {!!phoneNumber && <Text style={[styles.subtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{phoneNumber}</Text>}
    </View>
  );

  const titleRow = (
    <View style={[styles.titleRow, { flexDirection: rtl.rowDirection }]}>
      {Platform.OS === 'web' && (
        <PaneHeaderIconButton icon={rtl.mirrorIcon('chevron-back') as any} onPress={() => navigateTo('chat')} color={colors.text.primary as string} />
      )}
      <View style={[styles.titleInfo, rtl.marginStart(spacing.sm)]}>
        <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{contactName || phoneNumber || t('callHistory.title')}</Text>
        {!!phoneNumber && <Text style={[styles.subtitle, { textAlign: rtl.textStart }]}>{phoneNumber}</Text>}
      </View>
      {!!phoneNumber && (
        <TouchableOpacity style={styles.callButton} onPress={() => handleCallBack()} activeOpacity={0.85}>
          <Ionicons name="call" size={18} color="#FFFFFF" />
        </TouchableOpacity>
      )}
    </View>
  );

  const historyContent = (
    <>
      {isLoading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.primary as string} />
        </View>
      ) : calls.length === 0 ? (
        <View style={styles.centered}>
          <Ionicons name="call-outline" size={40} color={colors.text.disabled as string} />
          <Text style={[styles.emptyText, { textAlign: rtl.textStart }]}>{t('callHistory.noCalls')}</Text>
        </View>
      ) : (
        <FlatList
          data={calls}
          keyExtractor={(item) => item._id}
          contentContainerStyle={styles.list}
          renderItem={({ item }) => {
            const icon = getDirectionIcon(item);
            const statusLabel = t(STATUS_LABEL_KEY[item.status] ?? STATUS_LABEL_KEY.ended);
            const isSelected = item._id === selectedCallId;
            return (
              <TouchableOpacity
                style={[styles.callRow, { flexDirection: rtl.rowDirection }, isSelected && styles.callRowSelected]}
                onPress={() => setSelectedCallId(item._id)}
                activeOpacity={0.7}
              >
                <View style={[styles.callIconWrap, { backgroundColor: icon.color + '20' }]}>
                  <Ionicons name={icon.name} size={18} color={icon.color} />
                </View>
                <View style={styles.callInfo}>
                  <Text style={[styles.callDirection, { textAlign: rtl.textStart }]}>
                    {item.direction === 'inbound' ? t('callHistory.incoming') : t('callHistory.outgoing')} · {statusLabel}
                  </Text>
                  <Text style={[styles.callTime, { textAlign: rtl.textStart }]}>{formatDateTime(item.startedAt)}</Text>
                </View>
                <Text style={styles.callDuration}>{formatDuration(item.duration)}</Text>
              </TouchableOpacity>
            );
          }}
        />
      )}
    </>
  );

  const body = (
    <View style={styles.container}>
      {!embedded && titleRow}
      {historyContent}
    </View>
  );

  // Embedded (inside a contact profile's pane): no ScreenLayout, no nested
  // left/right panes — a pane can't sanely host another full pane split, and
  // a Modal popup here would cover the profile the user is already looking
  // at for no reason. Selecting a call instead drills down in place — the
  // list swaps for CallDetailsPanel (the same component the non-embedded
  // desktop right-pane branch below already uses), with a back row to
  // return to the list.
  if (embedded) {
    if (selectedCall) {
      return (
        <View style={{ flex: 1, height: '100%' }}>
          <PaneHeader
            leading={<PaneHeaderIconButton icon={rtl.mirrorIcon('chevron-back') as any} onPress={() => setSelectedCallId(null)} />}
            title={t('callHistory.details.title')}
          />
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: spacing.md }}>
            <CallDetailsPanel call={selectedCall} onCallBack={handleCallBack} onOutcomeRecorded={handleOutcomeRecorded} />
          </ScrollView>
        </View>
      );
    }
    return (
      <View style={{ flex: 1, height: '100%' }}>
        {!hideEmbeddedHeader && (
          <PaneHeader
            titleContent={titleContent}
            trailing={
              <View style={[styles.embeddedHeaderActions, { flexDirection: rtl.rowDirection }]}>
                {!!phoneNumber && (
                  <TouchableOpacity style={styles.embeddedCallButton} onPress={() => handleCallBack()} activeOpacity={0.85}>
                    <Ionicons name="call" size={18} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
                <PaneHeaderIconButton icon="close" onPress={onClose} color={colors.text.primary as string} />
              </View>
            }
          />
        )}
        {body}
      </View>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={false}
          showMessages={false}
          showNavigation={Platform.OS === 'web'}
          showBackButton={Platform.OS !== 'web'}
          onBackPress={() => navigateTo('chat')}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => setShowLeftPane(false)}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      rightPaneContent="custom"
      rightPaneCustomContent={
        selectedCall ? <CallDetailsPanel call={selectedCall} onCallBack={handleCallBack} onOutcomeRecorded={handleOutcomeRecorded} /> : (
          <View style={styles.detailsEmpty}>
            <Ionicons name="call-outline" size={32} color={colors.text.disabled as string} />
            <Text style={styles.detailsEmptyText}>{t('callHistory.selectCallHint')}</Text>
          </View>
        )
      }
    >
      {body}

      {/* Native has no right pane (showRightPane is web-only above) — without
          this, tapping a call row does nothing visible on native, and
          CallDetailsPanel (recording playback, call-back, and now outcome
          recording) would be entirely unreachable there. */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={!!selectedCall}
          onClose={() => setSelectedCallId(null)}
          title={t('callHistory.details.title')}
          scrollable
        >
          {selectedCall && (
            <CallDetailsPanel call={selectedCall} onCallBack={handleCallBack} onOutcomeRecorded={handleOutcomeRecorded} />
          )}
        </Modal>
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1 },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary as string,
  },
  titleInfo: { flex: 1 },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    marginTop: 2,
  },
  callButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  embeddedHeaderActions: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  embeddedCallButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary as string,
  },
  list: {
    padding: spacing.lg,
    gap: spacing.sm,
  },
  callRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderRadius: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  callRowSelected: {
    backgroundColor: (colors.primary as string) + '10',
  },
  callIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  callInfo: { flex: 1 },
  callDirection: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  callTime: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    marginTop: 2,
  },
  callDuration: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    fontVariant: ['tabular-nums'],
  },
  // Right pane call-details panel
  detailsContainer: {
    flex: 1,
    padding: spacing.lg,
  },
  detailsHeader: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  detailsIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  detailsDirection: {
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
  },
  detailsStatusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 12,
  },
  detailsStatusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
  },
  detailsRows: {
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  detailsRowLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  detailsRowValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  detailsCallButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary as string,
    borderRadius: 14,
    paddingVertical: spacing.md,
    gap: spacing.sm,
  },
  detailsCallButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
  },
  recordingSection: {
    marginBottom: spacing.lg,
  },
  outcomeSection: {
    marginBottom: spacing.lg,
  },
  outcomeOptionsRow: {
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  outcomeChip: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 999,
    alignSelf: 'flex-start',
  },
  outcomeChipSelectable: {
    borderWidth: 1,
  },
  outcomeChipText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
  },
  recordingSectionLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    marginBottom: spacing.sm,
  },
  recordingLoadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    backgroundColor: (colors.primary as string) + '10',
    alignSelf: 'flex-start',
  },
  recordingLoadButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.primary as string,
  },
  recordingUnavailableText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    fontStyle: 'italic' as const,
  },
  recordingPlayerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  recordingPlayerWrap: {
    flex: 1,
    backgroundColor: colors.background.secondary as string,
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
  },
  recordingDownloadButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (colors.primary as string) + '10',
  },
  detailsEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.lg,
  },
  detailsEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    textAlign: 'center',
  },
});
